import has from "mout/object/has"
import { db } from "../../../mp-database"
import parseSnapshot from "../../../mp-utils/generic-utils/common/parseSnapshot"
import fetchGroupRecord from "../fetchGroupRecord"
import { chunk } from "lodash"

/**
 * Firestore throws below error,
 * it usually happens when there are rapid subsequent requests to collections with relatively large data sets.
 * usually in forEach loops.
 *
 * [2020-06-09T15:40:43.991Z]  @firebase/firestore: Firestore (7.14.5): FirebaseError: [code=resource-exhausted]: Resource has been exhausted (e.g. check quota).
 * [2020-06-09T15:40:43.992Z]  @firebase/firestore: Firestore (7.14.5): Using maximum backoff delay to prevent overloading the backend.
 */

/**
 * Non-super-admin reads go through the interceptor Cloud Function, which fails with an
 * empty 500 (out of memory / response too large) when a large district's students are
 * requested in one call. The proxy turns that into an empty snapshot, so the roster
 * silently came back empty. Page by document id (no composite index needed).
 */
export const DISTRICT_STUDENTS_PAGE_SIZE = 1000

async function fetchDistrictStudents(districtId) {
  const students = {}
  let lastStudentId = null

  while (true) {
    let query = db
      .collection("students")
      .where("schoolDistrict", "==", districtId)
      .orderBy("__name__")

    if (lastStudentId) {
      query = query.startAfter(lastStudentId)
    }

    const snapshot = await query.limit(DISTRICT_STUDENTS_PAGE_SIZE).get()
    Object.assign(students, parseSnapshot(snapshot))

    if (snapshot.docs.length < DISTRICT_STUDENTS_PAGE_SIZE) break
    lastStudentId = snapshot.docs[snapshot.docs.length - 1].id
  }

  return students
}

export default async function initializeManageEducators(
  user,
  actions,
  groupId,
  setUnsubscribe
) {
  try {
    if (has(user, "role")) {
      if (user.role === "Super Administrator" && !groupId) return

      const groupRecord = await fetchGroupRecord(groupId || user.id)
      if (!groupRecord) return

      actions.initializing()

      //TODO need a function to eval the permission access

      const { groupType } = groupRecord

      //Maybe DRY below
      if (groupType === "School District") {
        actions.set.districts({ [groupRecord.id]: groupRecord })

        const schools = await db
          .collection("schools")
          .where("district", "==", groupRecord.id)
          .get()
        actions.set.schools(parseSnapshot(schools))

        actions.set.students(await fetchDistrictStudents(groupRecord.id))
      }

      if (groupType === "School") {
        actions.set.schools({ [groupRecord.id]: groupRecord })

        const collections = ["districts", "students"]

        for (const collection of collections) {
          let queryRef = db.collection(collection)

          if (collection === "districts") {
            queryRef = queryRef.where("id", "==", groupRecord.district)
          }

          if (collection === "students") {
            queryRef = queryRef.where("school", "==", groupRecord.id)
          }

          const data = await queryRef.get()
          actions.set[collection](parseSnapshot(data))
        }
      }

      if (groupType === "Teacher") {
        actions.set.educators({ [groupRecord.id]: groupRecord })

        const district = await db
          .collection("districts")
          .doc(groupRecord.schoolDistrict)
          .get()
          .then(doc => doc.data())

        actions.set.districts({ [district?.id]: district })

        const school = await db
          .collection("schools")
          .doc(groupRecord.school)
          .get()
          .then(doc => doc.data())

        school && actions.set.schools({ [school.id]: school })

        const classes = await db
          .collection("classes")
          .where("teacher", "==", groupRecord.id)
          .get()
          .then(snapshot => parseSnapshot(snapshot))

        actions.set.classes(classes)

        const studentIds = []

        for (const classId in classes) {
          const classDoc = classes[classId]

          if (classDoc.students) {
            for (const studentId in classDoc.students) {
              if (classDoc.students[studentId]) {
                studentIds.push(studentId)
              }
            }
          }
        }

        const studentIdsChunks = chunk(studentIds, 10)
        let students = {}

        for (let i = 0; i < studentIdsChunks.length; i++) {
          const studentIdsChunk = studentIdsChunks[i]
          const studentDocs = await db
            .collection("students")
            .where("id", "in", studentIdsChunk)
            .get()
            .then(snapshot => parseSnapshot(snapshot))
          students = { ...students, ...studentDocs }
        }

        actions.set.students(students)
      }
      if (groupType === "Class") {
        actions.set.classes({ [groupRecord.id]: groupRecord })

        const district = await db
          .collection("districts")
          .doc(groupRecord.district)
          .get()
          .then(doc => doc.data())

        actions.set.districts({ [district?.id]: district })

        const school = await db
          .collection("schools")
          .doc(groupRecord.school)
          .get()
          .then(doc => doc.data())

        school && actions.set.schools({ [school.id]: school })

        const educator = await db
          .collection("educators")
          .doc(groupRecord.teacher)
          .get()
          .then(doc => doc.data())
        actions.set.educators({ [educator.id]: educator })

        const studentIds = []

        if (groupRecord.students) {
          for (const studentId in groupRecord.students) {
            if (groupRecord.students[studentId]) {
              studentIds.push(studentId)
            }
          }
        }

        const studentIdsChunks = chunk(studentIds, 10)
        let students = {}

        for (let i = 0; i < studentIdsChunks.length; i++) {
          const studentIdsChunk = studentIdsChunks[i]
          const studentDocs = await db
            .collection("students")
            .where("id", "in", studentIdsChunk)
            .get()
            .then(snapshot => parseSnapshot(snapshot))
          students = { ...students, ...studentDocs }
        }

        actions.set.students(students)
      }

      actions.initializingSuccess()
    }
  } catch (error) {
    actions.initializingError(error)
    throw error
  }
}
